import { IFilterConfigItem } from '@/bundles/GroupFilters/interfaces/IFilterConfigItem';
import { FilterCategoryEnum, FilterTypeEnum } from '@/bundles/GroupFilters/enums/FilterTypeEnum';
import i18n from '@/bundles/Localization';
import { LocalizationEnum } from '@/bundles/Localization/enum/LocalizationEnum';
import { getPropertyCenterTypeTextValues } from '@/bundles/Property/enums/PropertyCenterTypeEnum';
import { sprinklerTypes } from '@/bundles/Properties/helpers';
import { getContactRolesBySource } from '@/bundles/Contact/helpers/cards/contactsCard';
import { Nullable } from '@/utils/types';
import { formatToSF } from '@/bundles/GroupFilters/helpers/formatters/formatToSF';
import {
  companyPropertyRoles,
  constructionStatuses,
  FilterPrimaryUseClassItems,
  getCountriesForFilters,
  getMarketsFilters,
  getPropertyTypesWithMixedUseForFilters,
  getPropertyUsesForFilters,
  PROPERTY_TYPE_KEY,
} from '@/bundles/GroupFilters/consts';
import {
  getCraneFiltersConfigByType,
  getLoadingAndDoorsFiltersConfigByType,
  getParkingFiltersConfigByType,
  getPropertyBuildingFiltersConfig,
  getPropertySiteFiltersConfig,
  getRailFiltersConfigByType,
  getRefrigerationFiltersConfigByType,
  getUtilitiesFiltersConfigByType
} from '@/bundles/GroupFilters/helpers/filtersConfig/reusable';
import { isYearRule } from '@/bundles/GroupFilters/helpers/rules';
import { getStaticSetFilterConfig } from '@/bundles/GroupFilters/helpers/filtersConfig/getStaticSetFilterConfig';
import { IFilterButtonGroupItems } from '@/bundles/GroupFilters/interfaces/inputs/IFilterButtonGroupItems';
import { AllExcludeOnlyLabels } from '@/bundles/GroupFilters/enums/AllExcludeOnlyEnum';
import { FilterPrimaryUseClassEnum } from '@/bundles/GroupFilters/types';
import { IGroupFilterItem } from '@/bundles/GroupFilters/helpers';
import { getShowArchiveFilterConfig } from '@/bundles/GroupFilters/helpers/filtersConfig/getShowArchiveFilterConfig';
import { getOccupancyTypeFilterItems } from '@/bundles/Occupier/helpers/getOccupancyTypeFilterItems';

export const getPropertyFilters = (filters?: Nullable<IGroupFilterItem>): IFilterConfigItem[] => {
  const propertyTypesWithMixedUse = getPropertyTypesWithMixedUseForFilters();
  const selectedPropertyTypes = filters?.[PROPERTY_TYPE_KEY]?.value || [];

  return [
    {
      title: 'Property Type',
      type: FilterTypeEnum.property_type_group,
      category: FilterCategoryEnum.property_info,
      children: [
        {
          title: 'Property Type',
          name: PROPERTY_TYPE_KEY,
          type: FilterTypeEnum.autocomplete_multiple_select,
          items: propertyTypesWithMixedUse,
        },
        {
          title: 'Include Property Type As Primary Use',
          name: 'property_type_as_primary_use',
          type: FilterTypeEnum.switch,
          defaultValue: true,
        },
      ],
    },
    {
      title: 'Property Use',
      type: FilterTypeEnum.property_use_group,
      category: FilterCategoryEnum.property_info,
      children: [
        {
          title: 'Property Use Class',
          name: 'property_use_class',
          type: FilterTypeEnum.button_group,
          items: FilterPrimaryUseClassItems,
          defaultValue: FilterPrimaryUseClassEnum.either,
        },
        {
          title: 'Property Use',
          name: 'property_primary_use',
          type: FilterTypeEnum.autocomplete_multiple_select,
          items: getPropertyUsesForFilters(),
        },
      ],
    },
    {
      title: 'Property Use Type(s)',
      name: 'property_use_type',
      type: FilterTypeEnum.use_type_multiple_select,
      category: FilterCategoryEnum.property_info,
    },
    {
      title: i18n.t(LocalizationEnum.retail_center_type) as string,
      items: getPropertyCenterTypeTextValues(),
      name: 'property_center_type',
      type: FilterTypeEnum.autocomplete_multiple_select,
      category: FilterCategoryEnum.property_info,
    },
    {
      title: 'Building Size',
      name: 'property_size',
      type: FilterTypeEnum.number_range,
      category: FilterCategoryEnum.property_info,
      formatter: (v: Nullable<number>) => formatToSF(v),
    },
    {
      title: 'Property Land Size',
      name: 'property_land_size',
      type: FilterTypeEnum.land_size_range, // sending with property_land_size_unit
      category: FilterCategoryEnum.property_info,
    },
    {
      title: '# of Floors',
      name: 'property_floors_count', // NEW
      type: FilterTypeEnum.number_range,
      category: FilterCategoryEnum.property_info,
    },
    {
      title: 'Construction Status',
      items: constructionStatuses,
      name: 'property_construction_status',
      type: FilterTypeEnum.autocomplete_multiple_select,
      category: FilterCategoryEnum.property_info,
    },
    {
      title: i18n.t(LocalizationEnum.property_year_built) as string,
      name: 'property_year_built', // NEW
      type: FilterTypeEnum.number_range,
      category: FilterCategoryEnum.property_info,
      additional: {
        minRules: [isYearRule],
        maxRules: [isYearRule],
        isRawFormat: true,
      },
      formatter: (v: Nullable<number>) => v?.toString() || '', // don't format
    },
    {
      title: i18n.t(LocalizationEnum.property_year_renovated) as string,
      name: 'property_year_renovated', // NEW
      type: FilterTypeEnum.number_range,
      category: FilterCategoryEnum.property_info,
      additional: {
        minRules: [isYearRule],
        maxRules: [isYearRule],
        isRawFormat: true,
      },
      formatter: (v: Nullable<number>) => v?.toString() || '', // don't format
    },
    {
      title: i18n.t(LocalizationEnum.target_completion_year) as string,
      type: FilterTypeEnum.target_completion_year_group,
      category: FilterCategoryEnum.property_info,
      children: [
        {
          title: i18n.t(LocalizationEnum.target_completion_year) as string,
          name: 'property_target_completion_year', // NEW
          type: FilterTypeEnum.number_range,
          additional: {
            minRules: [isYearRule],
            maxRules: [isYearRule],
            isRawFormat: true,
          },
          formatter: (v: Nullable<number>) => v?.toString() || '', // don't format
        },
        {
          title: i18n.t(LocalizationEnum.target_completion_quarter) as string,
          name: 'property_target_completion_quarter',
          items: ['Q1', 'Q2', 'Q3', 'Q4'],
          type: FilterTypeEnum.one_select,
        },
      ],
    },
    {
      title: 'Zoning',
      name: 'property_zoning', // NEW
      type: FilterTypeEnum.textfield,
      category: FilterCategoryEnum.property_info,
    },
    {
      title: '# Car Parks',
      name: 'property_parking_count', // NEW
      type: FilterTypeEnum.number_range,
      category: FilterCategoryEnum.property_info,
    },
    {
      title: 'Parking Ratio',
      name: 'property_parking_ratio', // NEW
      type: FilterTypeEnum.number_range,
      category: FilterCategoryEnum.property_info,
      additional: {
        suffix: '/ 1,000 SF',
      },
      formatter: (v: Nullable<number>) => v?.toString() || '', // don't format
    },
    {
      title: 'Sprinkler Type',
      items: sprinklerTypes,
      name: 'property_sprinklers', // NEW
      type: FilterTypeEnum.autocomplete_multiple_select,
      category: FilterCategoryEnum.property_info,
    },
    {
      title: 'Min Clear Height',
      name: 'property_min_clear_height',
      type: FilterTypeEnum.building_dimension_range,
      category: FilterCategoryEnum.property_info,
    },
    {
      title: 'Max Clear Height',
      name: 'property_max_clear_height',
      type: FilterTypeEnum.building_dimension_range,
      category: FilterCategoryEnum.property_info,
    },
    {
      title: '# Dock High Doors',
      name: 'property_dock_door_count',
      type: FilterTypeEnum.number_range,
      category: FilterCategoryEnum.property_info,
    },
    {
      title: '# Drive-In Doors',
      name: 'property_ground_door_count',
      type: FilterTypeEnum.number_range,
      category: FilterCategoryEnum.property_info,
    },
    {
      title: 'Property Comments',
      name: 'property_comments',
      type: FilterTypeEnum.textfield,
      category: FilterCategoryEnum.property_info,
    },
    {
      title: i18n.t(LocalizationEnum.legacy_id) as string,
      name: 'property_legacy_id',
      type: FilterTypeEnum.textfield,
      category: FilterCategoryEnum.property_info,
    },
    // FilterCategoryEnum.property_info end
    // FilterCategoryEnum.site start
    ...getPropertySiteFiltersConfig(),
    // FilterCategoryEnum.site end
    // FilterCategoryEnum.building start
    ...getPropertyBuildingFiltersConfig({
      propertyTypesWithMixedUse, type: 'property'
    }),
    // FilterCategoryEnum.building end
    // FilterCategoryEnum.crane start
    ...getCraneFiltersConfigByType(),
    // FilterCategoryEnum.crane end
    // FilterCategoryEnum.parking start
    ...getParkingFiltersConfigByType('property'),
    // FilterCategoryEnum.parking end
    // FilterCategoryEnum.rail start
    ...getRailFiltersConfigByType('property'),
    // FilterCategoryEnum.rail end
    // FilterCategoryEnum.refrigeration start
    ...getRefrigerationFiltersConfigByType('property'),
    // FilterCategoryEnum.refrigeration end
    // FilterCategoryEnum.doors start
    ...getLoadingAndDoorsFiltersConfigByType('property'),
    {
      title: i18n.t(LocalizationEnum.shared_truck_court) as string,
      name: `property_shared_truck_court`,
      items: <IFilterButtonGroupItems>Object.keys(AllExcludeOnlyLabels).map((key) => ({
        text: AllExcludeOnlyLabels[key],
        value: key,
      })),
      type: FilterTypeEnum.button_group,
      category: FilterCategoryEnum.doors,
    },

    // FilterCategoryEnum.doors end
    // FilterCategoryEnum.utilities start
    ...getUtilitiesFiltersConfigByType('property'),
    // FilterCategoryEnum.utilities end
    // FilterCategoryEnum.market_location start
    {
      title: 'Address',
      name: 'property_address',
      type: FilterTypeEnum.textfield,
      category: FilterCategoryEnum.market_location,
    },
    {
      title: 'City',
      name: 'property_location_city',
      type: FilterTypeEnum.city_autocomplete,
      category: FilterCategoryEnum.market_location,
      additional: {
        citiesSource: 'property',
      }
    },
    {
      title: 'State',
      name: 'property_location_state',
      type: FilterTypeEnum.states_autocomplete,
      category: FilterCategoryEnum.market_location,
    },
    {
      title: 'Country',
      name: 'property_location_country',
      type: FilterTypeEnum.autocomplete_multiple_select,
      items: getCountriesForFilters(),
      category: FilterCategoryEnum.market_location,
    },
    {
      title: 'Zip / Postal Code',
      name: 'property_location_postal_cd',
      type: FilterTypeEnum.postal_code_combobox,
      category: FilterCategoryEnum.market_location,
    },
    ...getMarketsFilters({ propertyTypes: selectedPropertyTypes }),
    {
      title: `Property ${i18n.t(LocalizationEnum.apn_number)}`,
      name: 'property_apn_number',
      type: FilterTypeEnum.textfield,
      category: FilterCategoryEnum.market_location,
    },
    // FilterCategoryEnum.market_location end
    // FilterCategoryEnum.company_contact start
    {
      title: 'Company Name',
      name: 'company_name',
      type: FilterTypeEnum.company_name_combobox,
      category: FilterCategoryEnum.company_contact,
    },
    {
      title: 'Company Property Role',
      items: companyPropertyRoles,
      name: 'company_property_role',
      type: FilterTypeEnum.autocomplete_multiple_select,
      category: FilterCategoryEnum.company_contact,
    },
    {
      title: 'Contact Name',
      name: 'contact_names',
      type: FilterTypeEnum.contact_names_multiple_select,
      category: FilterCategoryEnum.company_contact,
    },
    {
      title: 'Contact Company Role',
      items: getContactRolesBySource('company'),
      name: 'contact_company_role',
      type: FilterTypeEnum.autocomplete_multiple_select,
      category: FilterCategoryEnum.company_contact,
    },
    {
      title: 'Contact Property Role',
      items: getContactRolesBySource('property'),
      name: 'property_contact_role',
      type: FilterTypeEnum.autocomplete_multiple_select,
      category: FilterCategoryEnum.company_contact,
    },
    // FilterCategoryEnum.company_contact end
    // FilterCategoryEnum.occupier_filters start
    {
      title: 'Occupier Space Size',
      name: 'occupier_size',
      type: FilterTypeEnum.number_range,
      category: FilterCategoryEnum.occupier_filters,
      formatter: (v: Nullable<number>) => formatToSF(v),
    },
    {
      title: 'Occupancy Type',
      name: 'occupier_occupancy_type',
      type: FilterTypeEnum.autocomplete_multiple_select,
      items: getOccupancyTypeFilterItems(),
      category: FilterCategoryEnum.occupier_filters,
    },
    {
      title: 'Occupier Use Type(s)',
      name: 'occupier_use_type',
      type: FilterTypeEnum.use_type_multiple_select,
      category: FilterCategoryEnum.occupier_filters,
    },
    // FilterCategoryEnum.occupier_filters end
    // FilterCategoryEnum.created_edited start
    {
      title: 'Created/Edited Date',
      name: 'property_view_date',
      type: FilterTypeEnum.date_range,
      category: FilterCategoryEnum.created_edited,
    },
    {
      title: 'Created Date',
      name: 'property_created_date',
      type: FilterTypeEnum.date_range,
      category: FilterCategoryEnum.created_edited,
    },
    {
      title: 'Edited Date',
      name: 'property_edited_date',
      type: FilterTypeEnum.date_range,
      category: FilterCategoryEnum.created_edited,
    },
    {
      title: 'Created/Edited by',
      name: 'property_view',
      type: FilterTypeEnum.create_updated_by_select,
      category: FilterCategoryEnum.created_edited,
    },
    {
      title: 'Created by',
      name: 'property_created_by',
      type: FilterTypeEnum.create_updated_by_select,
      category: FilterCategoryEnum.created_edited,
    },
    {
      title: 'Edited by',
      name: 'property_edited_by',
      type: FilterTypeEnum.create_updated_by_select,
      category: FilterCategoryEnum.created_edited,
    },
    // FilterCategoryEnum.created_edited end
    getShowArchiveFilterConfig(),
    getStaticSetFilterConfig('property'),
  ].filter(Boolean) as IFilterConfigItem[]; // filter out null values
};
